<template>
  <section v-if="!promotionNotApplicable" class="DiscountTiers pb-7 mb-9">
    <header class="flex items-center justify-start gap-x-3 mt-6 mx-5 md:ml-0">
      <svg-icon-active-discount class="w-6 h-6" />
      <h3 class="font-body font-semibold text-lg">{{ $t('discountTiersTitle') }}</h3>
    </header>
    <div class="flex items-center justify-between font-body text-base uppercase mt-4 mb-3 mx-5 md:ml-0">
      <span class="text-gray-500">{{ amountRemaining }} {{ $t('egp') }} {{ $t('remainingAmount') }}</span>
      <span class="text-primary-700">{{
        progressDone === 100 ? $t('enjoyYourDiscount') : `${nextLevelAmount} ${$t('egp')}`
      }}</span>
    </div>
    <div class="ProgressBar mx-5 md:ml-0">
      <div class="bg-green-500 absolute top-0 left-0 bottom-0" :style="`width: ${progressDone}%`"></div>
    </div>
    <div class="flex items-center justify-between font-body text-base mt-3 mb-4 mx-5 md:ml-0">
      <span v-if="currentTier !== 0" class="text-gray-500">{{ $t('tierNo', [currentTier]) }}</span>
      <span class="text-primary-700" :class="{ 'ml-auto': currentTier === 0 }">{{
        $t('tierNo', [Math.min(currentTier + 1, cartPriceRuleDiscounts.tiers.length)])
      }}</span>
    </div>
    <p v-if="currentTier" class="mt-6 text-base text-gray-500 font-body mx-5 md:ml-0">
      {{
        progressDone === 100
          ? $t('enjoyYourDiscountBody')
          : $t('discountTiersMessage', [currentTier, amountRemaining, nextTierDiscount])
      }}
    </p>
    <p v-else class="mt-6 text-base text-gray-500 font-body mx-5 md:ml-0">
      {{ $t('noTierMessage', [amountRemaining, nextTierDiscount]) }}
    </p>
  </section>
</template>

<script lang="ts" setup>
const { subtotal } = useCartAttributes();
const { cartPriceRuleDiscounts } = useExtendedCartAttributes();
const { t: $t } = useI18n({ useScope: 'local' });

const nextLevelAmount = computed(() => {
  const eligibleTierIdx = cartPriceRuleDiscounts.value.eligibleTierIdx;

  // If there are no tiers, return 0
  if (!cartPriceRuleDiscounts.value.tiers || !cartPriceRuleDiscounts.value.tiers?.length) {
    return 0;
  }

  // If the last tier is eligible, return the last tier minimum subtotal
  if (eligibleTierIdx === cartPriceRuleDiscounts.value.tiers.length - 1) {
    return cartPriceRuleDiscounts.value.tiers?.[eligibleTierIdx]?.subtotalValue;
  }

  // If there are no eligible tiers or the first tier is eligible, return the first tier minimum subtotal
  if (eligibleTierIdx === 0) {
    return cartPriceRuleDiscounts.value.tiers?.[0]?.isApplied
      ? cartPriceRuleDiscounts.value.tiers?.[1]?.subtotalValue
      : cartPriceRuleDiscounts.value.tiers?.[0]?.subtotalValue;
  }

  // If the eligible tier is in the middle, return the next tier minimum subtotal
  return cartPriceRuleDiscounts.value.tiers?.[eligibleTierIdx + 1]?.subtotalValue;
});

const { amountRemaining, currentTier, nextTierDiscount, progressDone } = useGiftTiers(
  nextLevelAmount,
  subtotal,
  computed(() => cartPriceRuleDiscounts.value),
);

const promotionNotApplicable = computed(() => {
  // If current tier is 0 (i.e. no tier is applied) and subtotal surpasses minimum of first tier
  return currentTier.value === 0 && subtotal.value >= cartPriceRuleDiscounts.value.tiers?.[0]?.subtotalValue;
});
</script>

<style lang="postcss" scoped>
.ProgressBar {
  @apply relative bg-green-100 rounded-md;
  height: 2px;
}

.DiscountTiers {
  @apply relative;

  &::before {
    @apply bg-gray-100;
    content: '';
    position: absolute;
    top: 100%;
    left: 1.25rem;
    right: 1.25rem;
    height: 1px;

    @screen md {
      left: 0;
      right: 0;
    }
  }
}

.Gifts {
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }

  .Gift {
    max-width: 152px;
    &__img {
      background: linear-gradient(360deg, #ebebeb 0%, #f0f0f0 100%);
      box-shadow: 0px 0px 48px 0px rgba(0, 0, 0, 0.04);
    }
  }
}
</style>

<i18n>
{
  "en": {
    "discountTiersTitle": "Spend more Save more",
    "remainingAmount": "TO GO",
    "noTierMessage": "Hurry! You've got {0} EGP left to spend on your favorite product and and get {1}% off on your order!",
    "discountTiersMessage": "Great! You unlocked the discount for tier {0} & you're {1} EGP away from unlocking {2}% discount!",
    "enjoyYourDiscount": "Enjoy your discount",
    "enjoyYourDiscountBody": "Congrats, you've unlocked the highest discount!",
    "tierNo": "Tier {0}",
    "tierSoldOut": "Tier Sold Out"
  },
  "ar": {
    "discountTiersTitle": "أنفق أكثر واحصل على تخفيض أكبر",
    "remainingAmount": "متبقي",
    "noTierMessage": "أسرع! لديك {0} جم متبقي لتنفقها على منتجك المفضل وتحصل على تخفيض {1}% على طلبك!",
    "discountTiersMessage": "مبروك! لقد حصلت على التخفيض الخاص بالمستوى {0}. أضف {1} جنيه لعربتك لتحصل على تخفيض {2}%!",
    "enjoyYourDiscount": "استمتع بالتخفيض",
    "enjoyYourDiscountBody": "مبروك! لقد حصلت على أعلى تخفيض!",
    "tierNo": "المستوى {0}",
    "tierSoldOut": "لقد نفذ المستوى"
  }
}
</i18n>
